import { useCallback, useEffect, useState } from 'react';
import { dspLabel } from '../format';
import {
  type FilterOption,
  type OptionsScope,
  enqueueOptions,
  fetchScopedOptions,
  scopeParams,
  searchOptions,
} from './optionsBatcher';

export type { FilterOption };

/**
 * Stable refetch key over every scope field. JSON rather than a join, so
 * ['ab', 'c'] and ['a', 'bc'] can't collide. Absent and empty fields key the
 * same, matching how scopeParams sends them.
 */
export const scopeKey = (scope: OptionsScope): string =>
  JSON.stringify([
    scope.advertisers ?? [],
    scope.whitelabels ?? [],
    scope.clients ?? [],
    scope.ribeyeCampaignIds ?? [],
  ]);

/**
 * Remember every label a dropdown has been shown. Ribeye option values are ids,
 * so a selection that falls out of the current list (search term, scope change)
 * would otherwise render as its raw id in MultiSelectFilter's orphan row (#4614).
 */
export const rememberLabels = (cache: Map<string, string>, options: FilterOption[]): void => {
  for (const option of options) cache.set(option.value, option.label);
};

const labelCaches = new Map<string, Map<string, string>>();

/**
 * One dimension's label cache. Module-level rather than per-mount: Redux keeps a
 * selection across navigation, so a remounted filter bar can hold ids it hasn't
 * fetched yet. Keyed by dimension, since an id is only unique within one.
 */
export const labelCache = (dimension: string): Map<string, string> => {
  let cache = labelCaches.get(dimension);
  if (!cache) {
    cache = new Map();
    labelCaches.set(dimension, cache);
  }
  return cache;
};

interface Scope extends OptionsScope {
  /**
   * Client ids to scope this dimension's options to (Advertiser, Campaign,
   * Line Item) — selecting a Client narrows all three dropdowns to that
   * client's own entities, the same way `whitelabels` above narrows Client,
   * cascading exactly like the existing advertiser scope does for
   * Campaign/Line Item (flank#4737). Backend: ribeye-api#2506
   * (`narrowScopeToClients` in filterOptions.ts) intersects this with the
   * caller's ceiling via `resolveAdvertiserIdsForClients`, mirroring how
   * `narrowScopeToWhitelabels` does it for `whitelabels`.
   */
  clients?: string[];
  /**
   * Skip the unscoped-and-idle fetch for Campaign/Line Item (#4610) — opt-in,
   * not baked into the dimension, because this hook is shared with Custom
   * Dashboards. Its per-widget pickers (`DashboardBuilder.tsx`) and its own
   * filter bar's Campaign/Line Item (`DashboardFilterBar.tsx`) call this same
   * hook without an advertiser scope and need the unscoped list to populate —
   * deferring unconditionally left those permanently empty (Lucas, review).
   * Unified Reporting no longer needs it: its Campaign/Line Item list ribeye
   * entities (#4614), a cheap Postgres lookup rather than a warehouse scan.
   */
  deferUnscopedEntity?: boolean;
}

/**
 * Fetches the selectable options for a single filter dimension. Unscoped
 * dropdowns are coalesced (see optionsBatcher) into one `options-batch` call.
 * When a scope is supplied (Campaign / Line Item / Client), the options are
 * fetched narrowed by it and refetched whenever the selection
 * changes. Errors fall back to an empty list so a missing/failed dimension
 * never breaks the filter bar.
 */
/**
 * The warehouse stores DSP as a lowercase slug (`gam`, `dv360`, `simplifi`) and the
 * options endpoint returns it verbatim, so a raw dropdown reads "gam" while the KPI
 * cards beside it read "GAM" (flank#4178). `DSP_DISPLAY` already exists for exactly
 * this and says every surface should use it — the filter dropdowns were the one that
 * did not. Applied here rather than per filter bar so Unified Reporting and Custom
 * Dashboards cannot drift apart again.
 *
 * Only the label is mapped. `value` stays the raw slug for two independent reasons:
 * it is what gets sent back as the filter and must match the warehouse, and
 * MultiSelectFilter keys its orphan check on `option.value` against the raw
 * `selected` slugs — map the value and every selected DSP becomes an orphan, gets
 * re-appended, and the list duplicates while filtering breaks.
 *
 * Last-resort fallback only (flank#4469): the backend now relabels a
 * whitelabel-renamed dsp itself (`label !== value`), and that must win over
 * this static map — this blanket override used to apply even then, silently
 * replacing e.g. Bonneville's "Live Sports" back with the raw "TikTok", the
 * exact thing the Platform filter dropdown is never supposed to leak (found
 * live via Martin's retest, 2026-10-19). `label === value` is how the
 * backend already signals "no rename configured for this whitelabel" —
 * same convention `sourceDisplayName`'s own passthrough uses.
 */
export const withDisplayLabels = (dimension: string, options: FilterOption[]): FilterOption[] =>
  dimension === 'dsp' ? options.map(o => (o.label === o.value ? { ...o, label: dspLabel(o.value) } : o)) : options;

/**
 * Campaign and Line Item, unscoped and unsearched, are a full-table DISTINCT
 * scan on the warehouse (#4610 — 1.9-6.7GB per dimension, run on every page
 * load via the batch even though nobody had opened either dropdown yet).
 * Deferred until an advertiser scope narrows the query or the user searches —
 * both already route through a real request below; this only skips the
 * expensive unscoped-and-idle case.
 */
const DEFERRED_UNTIL_SCOPED = new Set(['campaign', 'line_item']);
export const isDeferredUntilScoped = (dimension: string): boolean => DEFERRED_UNTIL_SCOPED.has(dimension);

const useFilterOptions = (dimension: string, scope: Scope = {}) => {
  const [options, setOptions] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  // Typeahead term. The server caps the option list, so the match has to happen
  // server-side — filtering the fetched page can't reach values past the cap.
  const [search, setSearch] = useState('');

  const key = scopeKey(scope);
  const { deferUnscopedEntity = false } = scope;

  useEffect(() => {
    if (!dimension) return;
    let active = true;
    setLoading(true);

    const term = search.trim();
    const request = term
      ? searchOptions(dimension, term, scope)
      : Object.keys(scopeParams(scope)).length > 0
        ? fetchScopedOptions(dimension, scope)
        : deferUnscopedEntity && isDeferredUntilScoped(dimension)
          ? Promise.resolve([])
          : enqueueOptions(dimension);

    request
      .then(result => {
        const labelled = withDisplayLabels(dimension, result);
        // Before setOptions: labelOf only changes with the dimension, so the orphan
        // row re-reads the cache when `options` changes.
        rememberLabels(labelCache(dimension), labelled);
        if (active) setOptions(labelled);
      })
      .catch(() => {
        if (active) setOptions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // `key` captures the whole scope; re-fetch when the dimension, scope or
    // search term changes.
  }, [dimension, key, search, deferUnscopedEntity]);

  const labelOf = useCallback((value: string) => labelCache(dimension).get(value) ?? value, [dimension]);

  return { options, loading, search, setSearch, labelOf };
};

export default useFilterOptions;
